// The tablet/desktop account list on `#s01`: grouped connection sections in
// dense (single-line) layout, closing with `AccountsSummaryRows`. The row
// itself owns its facts because status is connection-level and type is
// account-level; a decorative column header would misrepresent that hierarchy.
import type { Account, CanonicalAccount } from '@/api/hooks/useAccounts';
import { groupAccountsByConnection, groupCanonicalAccountsForPresentation, ConnectionGroup } from './ConnectionGroup';
import { AccountsSummaryRows } from './AccountsSummaryRows';

export type AccountsTableProps =
  | { accounts: Account[]; canonicalAccounts?: never; showSummary?: boolean; showType?: boolean; className?: string }
  | { accounts?: never; canonicalAccounts: CanonicalAccount[]; showSummary?: boolean; showType?: boolean; className?: string };

export function AccountsTable(props: AccountsTableProps) {
  const { showSummary = true, className } = props;

  if (props.canonicalAccounts !== undefined) {
    const { canonicalAccounts } = props;
    const presentation = groupCanonicalAccountsForPresentation(canonicalAccounts);
    if (presentation.state === 'inconsistent') {
      return (
        <p role="status" data-testid="canonical-account-presentation-inconsistent">
          Account history is unavailable. Try again.
        </p>
      );
    }

    return (
      <div className={className} data-testid="accounts-table">
        <div className="space-y-4">
          {presentation.groups.map((group) => (
            <ConnectionGroup key={group.key} group={group} dense aggregateDisplay="canonical_unavailable" />
          ))}
        </div>
        {showSummary && <AccountsSummaryRows accounts={canonicalAccounts} aggregateDisplay="canonical_unavailable" className="mt-4" />}
      </div>
    );
  }

  const { accounts } = props;
  const groups = groupAccountsByConnection(accounts);

  return (
    <div className={className} data-testid="accounts-table">
      <div className="space-y-4">
        {groups.map((group) => (
          <ConnectionGroup key={group.key} group={group} dense />
        ))}
      </div>
      {showSummary && <AccountsSummaryRows accounts={accounts} className="mt-4" />}
    </div>
  );
}

export default AccountsTable;
